import { useEffect, useRef, useState } from "react";
import { useRecoilValueLoadable, useSetRecoilState } from "recoil";
// material
import {
  Alert,
  Box,
  Chip,
  Divider,
  Stack,
  Switch,
  Typography,
} from "@mui/material";

import { useOpenSnackbar } from "@hooks/useSnackbar";
import axios from "@utils/axios";
import { adminRefreshTick, adminSettingsQuery } from "@states/admin";

/**
 * 스위치 하나하나에 **무엇이 달라지는지**를 붙여 둔다.
 *
 * 키 이름(`identity.require_on_login`)만 보고 «로그인 때 인증» 까지는 읽히지만,
 * «전원인가 관리자만인가», «끄면 어떻게 되나» 는 안 읽힌다. 이 값들은 잘못 켜면
 * 아무도 못 들어오고 잘못 끄면 문이 열리는 것들이라, 고르는 자리에 설명이 있어야
 * 한다.
 */
const GATES: {
  key: string;
  title: string;
  detail: string;
  /** 비용. **끄고 켜는 판단에 가장 크게 작용하는 정보**라 따로 둔다. */
  cost?: string;
  /** 보안 말고 얻는 것. 이게 없으면 «왜 켜지» 를 설명할 수 없는 스위치가 있다. */
  benefit?: string;
  warning?: string;
}[] = [
  {
    key: "identity.require_on_login",
    title: "로그인 — 전원, 매번",
    detail:
      "누구든 메일의 로그인 링크를 누른 뒤 휴대폰 본인인증을 거쳐야 들어옵니다. 인증 결과는 그 링크 하나에만 붙으므로 다음 로그인에는 다시 인증합니다.",
    cost:
      "로그인 1회마다 문자 1건이 나갑니다. 로그인할 때마다 비용이 드는 유일한 스위치입니다.",
    benefit:
      "켜 두는 동안 로그인하는 분마다 본인인증 지문이 쌓입니다. 지문이 있어야 «이메일 찾기» 가 되므로, 한동안 켜 두는 것이 예전 가입자분들의 이메일 찾기를 되살리는 방법이기도 합니다.",
    // 비상 경로를 **파일 이름으로 적지 않는다.** 그 스크립트는 이 저장소에도 서버
    // 저장소에도 없어서(운영 기록 쪽에만 있다) 이름을 적으면 찾을 수 없는 길을
    // 알려 주는 셈이 된다. 누구에게 연락하는지만 적는다.
    warning:
      "본인인증 연동이 멈추면 이 스위치를 끄지 않는 한 아무도 로그인할 수 없습니다 — 관리자 본인도 못 들어와 여기서 끌 수조차 없게 됩니다. 그때는 개발팀이 서버에서 비상 로그인 링크를 직접 발급해야 합니다. 장애 때 가장 먼저 끄는 스위치입니다.",
  },
  {
    key: "identity.require_on_signup",
    title: "회원가입 신청",
    detail:
      "신청 화면에서 휴대폰 본인인증을 받습니다. 여기서 받은 지문은 승인될 때 계정으로 옮겨져, 그 사람이 나중에 이메일을 잊어도 «이메일 찾기» 로 찾을 수 있게 됩니다.",
  },
  {
    key: "identity.require_on_first_login",
    title: "처음 한 번 — 지문이 없는 사람만",
    detail:
      "본인인증 기록이 없는 계정에 다음 로그인에서 한 번만 인증을 받습니다. 그 자리에서 지문이 등록되고, 이후에는 묻지 않습니다. 「이메일 찾기」가 이 지문으로 사람을 찾으므로, 켜 두지 않으면 기존 회원은 이메일을 잊었을 때 스스로 찾을 방법이 없습니다.",
    warning:
      "지문이 없는 사람은 전원 대상입니다. 본인인증 연동이 멈추면 그 사람들이 로그인할 수 없으니, 장애 때는 이 스위치도 함께 끕니다.",
  },
  {
    key: "identity.require_for_admin_login",
    title: "관리자 로그인",
    detail:
      "관리자 등급인 사람만 로그인할 때 본인인증을 거칩니다. 등급 부여·회수 같은 행위마다 묻지 않는 이유는, 한 화면에서 여러 명을 처리할 때 인증을 그 횟수만큼 하게 되고 과금도 그만큼이기 때문입니다. 관문을 로그인 한 곳에 두면 그 세션 전체가 인증을 거친 것이 됩니다.",
    cost: "관리자 수가 적어 비용 영향은 작습니다.",
  },
];

export default function IdentityPanel() {
  const loadable = useRecoilValueLoadable(adminSettingsQuery);
  const bumpRefresh = useSetRecoilState(adminRefreshTick);
  const openSnackbar = useOpenSnackbar();
  const [busyKey, setBusyKey] = useState<string | null>(null);

  /**
   * ★ **다시 읽는 동안 마지막 값을 붙들고 있는다.**
   *
   * 예전에는 `loadable.state !== "hasValue"` 일 때 빈 배열을 썼다. 그러면 스위치
   * 하나를 켠 순간 다시 읽기가 시작되고, 그 사이 `valueOf()` 가 **세 개 모두
   * `false`** 를 돌려준다 — 화면에서는 **세 스위치가 한꺼번에 꺼졌다 돌아온다.**
   *
   * 저장은 분리돼 있는데(스위치마다 자기 키만 PUT 한다) 보는 사람은 «셋이 같이
   * 움직인다» 고 읽게 된다. 실제로 그 보고를 받았다. 값이 엮인 것이 아니라
   * **화면이 잠깐 아무 값도 없는 상태를 보여 준 것**이다.
   */
  const lastKnown = useRef<typeof loadable.contents>([]);
  useEffect(() => {
    if (loadable.state === "hasValue") lastKnown.current = loadable.contents;
  }, [loadable.state, loadable.contents]);
  const settings =
    loadable.state === "hasValue" ? loadable.contents : lastKnown.current;
  const valueOf = (key: string) =>
    settings.find((item) => item.key === key)?.value === "true";
  const isDefault = (key: string) =>
    settings.find((item) => item.key === key)?.isDefault ?? true;

  const toggle = async (key: string, next: boolean) => {
    setBusyKey(key);
    try {
      await axios.put(`admin/settings/${encodeURIComponent(key)}`, {
        value: next ? "true" : "false",
      });
      // 켜고 끈 결과를 **다시 읽어서** 보여준다. 응답만 믿고 화면 상태를 바꾸면,
      // 저장이 실패했는데 켜진 것처럼 보이는 상태가 생긴다. 이 값들은 그렇게
      // 어긋나면 «막았다» 고 믿는 채로 문이 열려 있게 된다.
      bumpRefresh((tick) => tick + 1);
      openSnackbar(next ? "본인인증을 켰습니다." : "본인인증을 껐습니다.", "success");
    } catch (error: any) {
      openSnackbar(
        typeof error?.detail === "string" ? error.detail : "설정을 바꾸지 못했습니다.",
        "error",
      );
      bumpRefresh((tick) => tick + 1);
    } finally {
      setBusyKey(null);
    }
  };

  return (
    <Stack spacing={2}>
      <Typography variant="subtitle1">본인인증 수준</Typography>

      <Alert severity="info">
        여기서 켠 것은 <b>바로 적용됩니다.</b> 배포를 기다리지 않습니다 — 연동이 멈췄을
        때 즉시 끌 수 있어야 하기 때문입니다.
      </Alert>

      {loadable.state === "hasError" && (
        <Alert severity="error">설정을 불러오지 못했습니다.</Alert>
      )}

      <Box sx={{ border: 1, borderColor: "divider", borderRadius: 1 }}>
        {GATES.map((gate, index) => (
          <Box key={gate.key}>
            {index > 0 && <Divider />}
            <Stack direction="row" spacing={2} sx={{ p: 2 }} alignItems="flex-start">
              <Stack spacing={0.5} sx={{ flexGrow: 1 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="subtitle2">{gate.title}</Typography>
                  {/* 표에 값이 없어서 기본값을 쓰고 있는 상태를 구분해 보여준다.
                      «아직 아무도 정한 적 없음» 과 «꺼 두기로 정함» 은 다르다. */}
                  {isDefault(gate.key) && (
                    <Chip size="small" variant="outlined" label="기본값" />
                  )}
                </Stack>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  {gate.detail}
                </Typography>
                {/* 비용은 **끄고 켜는 판단에 가장 크게 작용하는 정보**다. 설명
                    문단에 묻어 두면 안 읽힌다. */}
                {gate.cost && (
                  <Typography variant="caption" sx={{ color: "warning.dark" }}>
                    💰 {gate.cost}
                  </Typography>
                )}
                {gate.benefit && (
                  <Typography variant="caption" sx={{ color: "info.dark" }}>
                    ↗ {gate.benefit}
                  </Typography>
                )}
                {gate.warning && valueOf(gate.key) && (
                  <Alert severity="warning" sx={{ mt: 1 }}>
                    {gate.warning}
                  </Alert>
                )}
              </Stack>
              {/* 누른 것만 잠근다. 셋을 다 잠그면 하나를 만질 때마다 나머지가
                  같이 회색이 되어, 값이 엮여 있는 것처럼 보인다. 서로 다른 키라
                  동시에 눌러도 부딪히지 않는다. */}
              <Switch
                checked={valueOf(gate.key)}
                disabled={busyKey === gate.key}
                onChange={(event) => toggle(gate.key, event.target.checked)}
              />
            </Stack>
          </Box>
        ))}
      </Box>

      <Typography variant="caption" sx={{ color: "text.secondary" }}>
        «이메일 찾기» 는 여기에 없습니다. 이메일을 모르는 사람에게는 로그인 링크를 보낼
        수도 없어서, 본인인증 말고 통과할 방법이 구조적으로 없습니다 — 끄면 그 기능이
        사라집니다.
      </Typography>
    </Stack>
  );
}
